import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import {
  AuthProvider,
  useAuth,
} from "./lib/AuthContext";
import { ThemeProvider } from "./lib/ThemeContext";
import { api } from "./lib/api";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import EmployerProfileImageUploader from "./components/EmployerProfileImageUploader";

import Home from "./pages/Home";
import Vacancies from "./pages/Vacancies";
import VacancyDetail from "./pages/VacancyDetail";
import Careers from "./pages/Careers";
import RoleGuide from "./pages/RoleGuide";
import ApplicationResources from "./pages/ApplicationResources";
import DepotFinder from "./pages/DepotFinder";
import Operators from "./pages/Operators";
import OperatorDetail from "./pages/OperatorDetail";
import ForEmployers from "./pages/ForEmployers";
import EmployerBusinessCaseBuilder from "./pages/EmployerBusinessCaseBuilder";
import EmployerLogin from "./pages/EmployerLogin";
import EmployerRegister from "./pages/EmployerRegister";
import EmployerDashboard from "./pages/EmployerDashboard";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TalentPoolRegister from "./pages/TalentPoolRegister";
import TalentPoolVerify from "./pages/TalentPoolVerify";
import TalentPoolSetPassword from "./pages/TalentPoolSetPassword";
import TalentPoolLogin from "./pages/TalentPoolLogin";
import TalentPoolForgotPassword from "./pages/TalentPoolForgotPassword";
import TalentPoolResetPassword from "./pages/TalentPoolResetPassword";
import TalentPoolDashboard from "./pages/TalentPoolDashboard";
import TalentPoolStageTwoAssessment from "./pages/TalentPoolStageTwoAssessment";
import TalentPoolCandidatesExplorer from "./pages/TalentPoolCandidatesExplorer";
import ExtendVacancy from "./pages/ExtendVacancy";
import FAQs from "./pages/FAQs";
import About from "./pages/About";
import TrainDriverJobs from "./pages/TrainDriverJobs";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Maintenance from "./pages/Maintenance";
import CompanyProfile from "./pages/CompanyProfile";
import RegionJobs from "./pages/RegionJobs";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancelled from "./pages/CheckoutCancelled";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminStageTwoDashboard from "./pages/AdminStageTwoDashboard";

import "./App.css";

const MAINTENANCE_MODE =
  process.env.REACT_APP_MAINTENANCE_MODE ===
  "true";

const MAINTENANCE_BYPASS_KEY =
  "rcuk-2026";

const VISITOR_HEARTBEAT_INTERVAL_MS =
  60000;

const VISITOR_ID_STORAGE_KEY =
  "rc_visitor_id";

function shouldTrackVisitorPath(
  pathname
) {
  const path =
    (
      pathname || ""
    ).toLowerCase();

  if (!path.startsWith("/")) {
    return false;
  }

  if (
    path.startsWith(
      "/admin"
    )
  ) {
    return false;
  }

  if (
    path.startsWith(
      "/employer-portal"
    )
  ) {
    return false;
  }

  if (
    path ===
    "/employers"
  ) {
    return false;
  }

  return true;
}

function getVisitorId() {
  if (
    typeof window ===
    "undefined"
  ) {
    return null;
  }

  const existing =
    localStorage.getItem(
      VISITOR_ID_STORAGE_KEY
    );

  if (existing) {
    return existing;
  }

  const created =
    typeof window.crypto
      ?.randomUUID ===
    "function"
      ? window.crypto.randomUUID()
      : `v-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 12)}`;

  localStorage.setItem(
    VISITOR_ID_STORAGE_KEY,
    created
  );

  return created;
}

function isBypassed() {
  if (
    typeof window ===
    "undefined"
  ) {
    return false;
  }

  try {
    const params =
      new URLSearchParams(
        window.location.search
      );

    if (
      params.get(
        "bypass"
      ) ===
      MAINTENANCE_BYPASS_KEY
    ) {
      localStorage.setItem(
        "rc_maint_bypass",
        "1"
      );

      return true;
    }

    return (
      localStorage.getItem(
        "rc_maint_bypass"
      ) === "1"
    );
  } catch {
    return false;
  }
}

function ProtectedRoute({
  children,
  role,
}) {
  const {
    user,
    loading,
  } = useAuth();

  const location =
    useLocation();

  if (loading) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!user) {
    const loginPath =
      role === "admin"
        ? "/admin/login"
        : role ===
            "talent_pool_member"
          ? "/talent-pool/login"
          : "/employer-portal/login";

    return (
      <Navigate
        to={loginPath}
        state={{
          from: location,
        }}
        replace
      />
    );
  }

  if (
    role &&
    user.role !== role &&
    user.role !== "admin"
  ) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return children;
}

function RedirectToHome() {
  const location =
    useLocation();

  return (
    <Navigate
      to={{
        pathname: "/",
        search:
          location.search,
      }}
      replace
    />
  );
}

function Shell() {
  const location =
    useLocation();

  useEffect(() => {
    if (
      typeof window ===
      "undefined"
    ) {
      return undefined;
    }

    if (
      !shouldTrackVisitorPath(
        location.pathname
      )
    ) {
      return undefined;
    }

    const visitorId =
      getVisitorId();

    if (!visitorId) {
      return undefined;
    }

    const sendHeartbeat =
      async () => {
        if (
          document.visibilityState !==
          "visible"
        ) {
          return;
        }

        try {
          await api.post(
            "/public/visitor-heartbeat",
            {
              visitor_id:
                visitorId,

              path:
                location.pathname,
            }
          );
        } catch {
          // Best effort only; metrics card has fallback behaviour.
        }
      };

    sendHeartbeat();

    const intervalId =
      window.setInterval(
        sendHeartbeat,
        VISITOR_HEARTBEAT_INTERVAL_MS
      );

    return () => {
      window.clearInterval(
        intervalId
      );
    };
  }, [
    location.pathname,
  ]);

  if (
    MAINTENANCE_MODE &&
    !isBypassed() &&
    !location.pathname.startsWith(
      "/admin"
    )
  ) {
    return (
      <>
        <ScrollToTop />
        <Maintenance />
      </>
    );
  }

  const hideChrome =
    location.pathname.startsWith(
      "/employer-portal/dashboard"
    ) ||
    location.pathname.startsWith(
      "/admin/dashboard"
    );

  const employerDashboard =
    location.pathname.startsWith(
      "/employer-portal/dashboard"
    );

  return (
    <div className="App min-h-screen flex flex-col">
      <ScrollToTop />

      {!hideChrome && (
        <Navbar />
      )}

      {employerDashboard && (
        <EmployerProfileImageUploader />
      )}

      <main className="flex-1">
        <Routes>
          <Route
            path="/"
            element={
              <Vacancies />
            }
          />

          <Route
            path="/vacancies"
            element={
              <RedirectToHome />
            }
          />

          <Route
            path="/vacancies/:id"
            element={
              <VacancyDetail />
            }
          />

          <Route
            path="/careers"
            element={
              <Careers />
            }
          />

          <Route
            path="/careers/:slug"
            element={
              <RoleGuide />
            }
          />

          <Route
            path="/application-resources"
            element={
              <ApplicationResources />
            }
          />

          <Route
            path="/depot-finder"
            element={
              <DepotFinder />
            }
          />

          <Route
            path="/train-driver-jobs"
            element={
              <TrainDriverJobs />
            }
          />

          <Route
            path="/faqs"
            element={
              <FAQs />
            }
          />

          <Route
            path="/talent-pool/register"
            element={
              <TalentPoolRegister />
            }
          />

          <Route
            path="/talent-pool/verify"
            element={
              <TalentPoolVerify />
            }
          />

          <Route
            path="/talent-pool/set-password"
            element={
              <TalentPoolSetPassword />
            }
          />

          <Route
            path="/talent-pool/login"
            element={
              <TalentPoolLogin />
            }
          />

          <Route
            path="/talent-pool/forgot-password"
            element={
              <TalentPoolForgotPassword />
            }
          />

          <Route
            path="/talent-pool/reset-password"
            element={
              <TalentPoolResetPassword />
            }
          />

          <Route
            path="/talent-pool/dashboard"
            element={
              <ProtectedRoute role="talent_pool_member">
                <TalentPoolDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/talent-pool/stage-2"
            element={
              <ProtectedRoute role="talent_pool_member">
                <TalentPoolStageTwoAssessment />
              </ProtectedRoute>
            }
          />

          <Route
            path="/operators"
            element={
              <Operators />
            }
          />

          <Route
            path="/operators/:slug"
            element={
              <OperatorDetail />
            }
          />

          <Route
            path="/companies/:slug"
            element={
              <CompanyProfile />
            }
          />

          <Route
            path="/jobs/:slug"
            element={
              <RegionJobs />
            }
          />

          <Route
            path="/employers"
            element={
              <ForEmployers />
            }
          />

          <Route
            path="/for-employers"
            element={
              <ForEmployers />
            }
          />

          <Route
            path="/for-employers/business-case-builder"
            element={
              <EmployerBusinessCaseBuilder />
            }
          />

          <Route
            path="/employer-portal/login"
            element={
              <EmployerLogin />
            }
          />

          <Route
            path="/employer-portal/register"
            element={
              <EmployerRegister />
            }
          />

          <Route
            path="/employer-portal/verify"
            element={
              <VerifyEmail />
            }
          />

          <Route
            path="/employer-portal/dashboard"
            element={
              <ProtectedRoute role="employer">
                <EmployerDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/talent-pool/candidates"
            element={
              <ProtectedRoute role="employer">
                <TalentPoolCandidatesExplorer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <ForgotPassword />
            }
          />

          <Route
            path="/reset-password"
            element={
              <ResetPassword />
            }
          />

          <Route
            path="/vacancy/extend"
            element={
              <ExtendVacancy />
            }
          />

          <Route
            path="/checkout/success"
            element={
              <CheckoutSuccess />
            }
          />

          <Route
            path="/checkout/cancelled"
            element={
              <CheckoutCancelled />
            }
          />

          <Route
            path="/about"
            element={
              <About />
            }
          />

          <Route
            path="/about/railway-careers"
            element={
              <Home />
            }
          />

          <Route
            path="/privacy"
            element={
              <Privacy />
            }
          />

          <Route
            path="/terms"
            element={
              <Terms />
            }
          />

          <Route
            path="/admin/login"
            element={
              <AdminLogin />
            }
          />

          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute role="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/stage-2"
            element={
              <ProtectedRoute role="admin">
                <AdminStageTwoDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />
        </Routes>
      </main>

      {!hideChrome && (
        <Footer />
      )}

      {!hideChrome && (
        <CookieBanner />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />

          <Toaster
            richColors
            position="top-center"
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}